import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { personalInfo } from '../mock';
import { useSectionNav } from '../hooks/useSectionNav';
import { landingPath, legalPath, isValidLang, DEFAULT_LANG, LANDING_PAGES, LEGAL_PAGES } from '../seo/seoConfig';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const scrollToSection = useSectionNav();
  const currentYear = new Date().getFullYear();
  const lang = (i18n.language || DEFAULT_LANG).slice(0, 2);
  const safeLang = isValidLang(lang) ? lang : DEFAULT_LANG;
  const linkClass = 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors';

  return (
    <footer className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-3">
              {personalInfo.name}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {personalInfo.role} | {personalInfo.location}
            </p>
            <div className="flex space-x-4">
              <a href={`mailto:${personalInfo.email}`} className={linkClass} aria-label="Email">
                <Mail className="h-5 w-5" />
              </a>
              <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className={linkClass} aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
            <address className="not-italic text-sm text-gray-600 dark:text-gray-400 mt-4 space-y-1" data-testid="footer-address">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" /> {t('footer.location')}</p>
              <p>{t('footer.serviceRegion')}</p>
            </address>
          </div>

          {/* Quick Links */}
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Quick Links
            </p>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('about')} className={linkClass}>About Me</button>
              </li>
              <li>
                <button onClick={() => scrollToSection('projects')} className={linkClass}>Projects</button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className={linkClass}>Services</button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className={linkClass}>Contact</button>
              </li>
              {Object.entries(LANDING_PAGES).map(([key, page]) => (
                <li key={key}>
                  <Link to={landingPath(safeLang, key)} className={linkClass} data-testid={key === 'haegendorf' ? 'footer-local-page-link' : `footer-${key}-page-link`}>
                    {t(page.footerKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Services
            </p>
            <ul className="space-y-2">
              <li className="text-gray-600 dark:text-gray-400">Custom Business Apps</li>
              <li className="text-gray-600 dark:text-gray-400">Internal Tools</li>
              <li className="text-gray-600 dark:text-gray-400">Process Automation</li>
              <li className="text-gray-600 dark:text-gray-400">Web & Desktop Solutions</li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            © {currentYear} {personalInfo.name}. All rights reserved.
          </p>
          <nav className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm" aria-label="Legal" data-testid="footer-legal-links">
            {Object.entries(LEGAL_PAGES).map(([key, page]) => (
              <Link key={key} to={legalPath(safeLang, key)} className={linkClass} data-testid={`footer-${key}-link`}>
                {t(page.footerKey)}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
